import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Tag, Typography, Card, Progress, Space, Alert,
  Row, Col, Statistic,
} from 'antd';
import {
  WarningOutlined, CheckCircleOutlined, MailOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { theme as antdTheme } from 'antd';
import axios from 'axios';
import AdminDateFilter, { resolveRange } from '../../components/AdminDateFilter';

const { Title, Text } = Typography;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const BOUNCE_WARN  = 5;
const BOUNCE_BLOCK = 10;

const AdminBounceReport = () => {
  const { token: tk } = antdTheme.useToken();
  const navigate      = useNavigate();

  const [data, setData]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [preset, setPreset]           = useState('All');
  const [customRange, setCustomRange] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = resolveRange(preset, customRange);
      const params = {};
      if (from) params.from = from;
      if (to)   params.to   = to;
      const r = await axios.get('/api/admin/bounce-report', { headers: authHeader(), params });
      setData(r.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [preset, customRange]);

  useEffect(() => { load(); }, [load]);

  const atRisk   = data.filter(r => r.bounce_rate >= BOUNCE_WARN && r.bounce_rate < BOUNCE_BLOCK);
  const critical = data.filter(r => r.bounce_rate >= BOUNCE_BLOCK);
  const healthy  = data.filter(r => r.bounce_rate < BOUNCE_WARN && r.total_sent > 0);

  const bounceColor = (rate) => {
    if (rate >= BOUNCE_BLOCK) return '#ef4444';
    if (rate >= BOUNCE_WARN)  return '#f59e0b';
    return '#10b981';
  };

  const columns = [
    {
      title: 'Organisation',
      key: 'org',
      render: (_, r) => (
        <Space>
          {r.bounce_rate >= BOUNCE_BLOCK && <WarningOutlined style={{ color: '#ef4444' }} />}
          {r.bounce_rate >= BOUNCE_WARN && r.bounce_rate < BOUNCE_BLOCK && <WarningOutlined style={{ color: '#f59e0b' }} />}
          <Text
            style={{ color: tk.colorPrimary, cursor: 'pointer', fontWeight: 500 }}
            onClick={() => navigate(`/admin/orgs/${r.org_id}`)}
          >
            {r.org_name}
          </Text>
          {r.suspended && <Tag color="error" style={{ fontSize: 11 }}>Suspended</Tag>}
        </Space>
      ),
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      width: 90,
      render: p => <Tag color={p === 'pro' ? 'gold' : 'default'}>{p?.toUpperCase()}</Tag>,
    },
    {
      title: 'Emails Sent',
      dataIndex: 'total_sent',
      key: 'total_sent',
      width: 110,
      align: 'center',
      render: v => <Text strong>{v.toLocaleString()}</Text>,
    },
    {
      title: 'Bounced',
      dataIndex: 'bounced',
      key: 'bounced',
      width: 90,
      align: 'center',
      render: (v, r) => (
        <Text style={{ color: v > 0 ? bounceColor(r.bounce_rate) : tk.colorTextSecondary, fontWeight: 600 }}>
          {v.toLocaleString()}
        </Text>
      ),
    },
    {
      title: 'Bounce Rate',
      key: 'bounce_rate',
      width: 160,
      sorter: (a, b) => a.bounce_rate - b.bounce_rate,
      defaultSortOrder: 'descend',
      render: (_, r) => r.total_sent > 0 ? (
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          <Text style={{ fontSize: 12, fontWeight: 700, color: bounceColor(r.bounce_rate) }}>
            {r.bounce_rate}%
          </Text>
          <Progress
            percent={Math.min(r.bounce_rate, 100)}
            showInfo={false}
            strokeColor={bounceColor(r.bounce_rate)}
            size="small"
          />
        </Space>
      ) : <Text type="secondary">No sends</Text>,
    },
    {
      title: 'Open Rate',
      key: 'open_rate',
      width: 140,
      render: (_, r) => r.total_sent > 0 ? (
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          <Text style={{ fontSize: 12, color: '#10b981' }}>{r.open_rate}%</Text>
          <Progress percent={Math.min(r.open_rate, 100)} showInfo={false} strokeColor="#10b981" size="small" />
        </Space>
      ) : <Text type="secondary">—</Text>,
    },
  ];

  return (
    <div style={{ padding: '0 2px' }}>
      <div style={{ marginBottom: 20 }}>
        <Title level={4} style={{ marginBottom: 4 }}>Bounce & Deliverability Report</Title>
        <Text type="secondary">Bounce rates per organisation. Flag orgs exceeding thresholds.</Text>
      </div>

      <AdminDateFilter
        preset={preset}
        setPreset={setPreset}
        customRange={customRange}
        setCustomRange={setCustomRange}
        onRefresh={load}
        loading={loading}
      />

      {critical.length > 0 && (
        <Alert
          type="error"
          showIcon
          icon={<WarningOutlined />}
          message={`${critical.length} organisation${critical.length > 1 ? 's' : ''} have bounce rates above ${BOUNCE_BLOCK}% — consider suspending them.`}
          style={{ marginBottom: 16, borderRadius: 10 }}
        />
      )}
      {atRisk.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`${atRisk.length} organisation${atRisk.length > 1 ? 's' : ''} are approaching the ${BOUNCE_BLOCK}% bounce threshold.`}
          style={{ marginBottom: 16, borderRadius: 10 }}
        />
      )}

      {/* Summary cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {[
          { title: 'Critical (≥10%)',  value: critical.length, color: '#ef4444', icon: <WarningOutlined /> },
          { title: 'At Risk (5–10%)',  value: atRisk.length,   color: '#f59e0b', icon: <WarningOutlined /> },
          { title: 'Healthy (<5%)',    value: healthy.length,  color: '#10b981', icon: <CheckCircleOutlined /> },
          { title: 'No Sends Yet',
            value: data.length - critical.length - atRisk.length - healthy.length,
            color: tk.colorTextQuaternary, icon: <MailOutlined /> },
        ].map(s => (
          <Col xs={12} sm={6} key={s.title}>
            <Card size="small" style={{ borderRadius: 10, border: `1px solid ${tk.colorBorderSecondary}` }}>
              <Statistic
                title={<Text type="secondary" style={{ fontSize: 12 }}>{s.title}</Text>}
                value={s.value}
                valueStyle={{ color: s.color, fontSize: 22, fontWeight: 700 }}
                prefix={React.cloneElement(s.icon, { style: { fontSize: 14, color: s.color, marginRight: 4 } })}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Card
        style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}` }}
        styles={{ body: { padding: 0 } }}
      >
        <Table
          rowKey="org_id"
          dataSource={data}
          columns={columns}
          loading={loading}
          size="small"
          pagination={{
            pageSize: 25,
            showTotal: n => `${n} organisations`,
            style: { padding: '12px 20px', margin: 0 },
          }}
          rowClassName={r => r.bounce_rate >= BOUNCE_BLOCK ? 'ant-table-row-danger' : ''}
          scroll={{ x: 'max-content' }}
          style={{ borderRadius: '0 0 12px 12px', overflow: 'hidden' }}
        />
      </Card>

      {/* Legend */}
      <div style={{ marginTop: 12, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        {[
          { color: '#10b981', label: 'Healthy (< 5%)' },
          { color: '#f59e0b', label: 'At Risk (5–10%)' },
          { color: '#ef4444', label: 'Critical (> 10%)' },
        ].map(l => (
          <Space key={l.label} size={6}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: l.color }} />
            <Text type="secondary" style={{ fontSize: 12 }}>{l.label}</Text>
          </Space>
        ))}
      </div>
    </div>
  );
};

export default AdminBounceReport;
